import { ChangeDetectorRef, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DateTime } from 'luxon';
import { interval } from 'rxjs';
import { tap } from 'rxjs/operators';

import { StorageService } from 'app/core/storage/storage.service';
import { CountdownModes } from 'app/shared/components/countdown/countdown-modes';
import { CountdownTimeLeft } from 'app/shared/components/countdown/countdown-time-left';
import { Sizes } from 'app/shared/constants/sizes';

@UntilDestroy()
@Component({
  selector: 'kh-countdown',
  templateUrl: './countdown.component.html',
  styleUrls: ['./countdown.component.less'],
  standalone: false
})
export class CountdownComponent implements OnInit {
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly storageService = inject(StorageService);

  private endDate!: DateTime;
  private startDate!: DateTime;

  public timeLeft: CountdownTimeLeft = {
    days: 0, hours: 0, minutes: 0, seconds: 0
  };

  @Input() public isLocalTime = false;
  @Input() public label?: string;
  @Input() public size?: Sizes;
  @Input() public mode?: CountdownModes; // text = 7d 5h 3m 30s, table
  @Input() public classes?: string; // class names to add to the container div for custom styling
  @Input() public isTimerCompleted?: boolean;
  @Output() public isTimerCompletedChange = new EventEmitter<boolean>();

  @Input() public set targetDate(date: string) {
    this.endDate = DateTime.fromISO(date);
  }

  public get noDays(): boolean {
    return this.timeLeft.days === 0 && this.timeLeft.hours === 0;
  }

  public get isTable(): boolean {
    return this.mode === 'table';
  }

  public ngOnInit(): void {
    this.runTimer();
  }

  private runTimer(): void {
    interval(1000)
      .pipe(
        tap(() => {
          const defaultTimerValue = {
            days: 0, hours: 0, minutes: 0, seconds: 0
          };

          this.startDate = DateTime.fromJSDate(this.storageService.getDate());

          if (this.endDate <= this.startDate) {
            this.isTimerCompleted = true;
            this.isTimerCompletedChange.emit(true);
            this.timeLeft = defaultTimerValue;

            this.changeDetectorRef.markForCheck();

            return;
          }

          this.timeLeft = (isNaN(+this.endDate) || isNaN(+this.startDate))
            ? defaultTimerValue
            : this.endDate.diff(this.startDate, ['days', 'hours', 'minutes', 'seconds', 'milliseconds']);

          this.changeDetectorRef.markForCheck();
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }
}
